<template>
    <component
        v-bind="attrs"
        :is="tagName"
        ref="button"
        class="sbs-base-button"
        :class="buttonClasses"
    >
        <slot></slot>
    </component>
</template>

<script>
/**
 * Компонент базовой кнопки/ссылки
 */
export default {
    name: 'SbsBaseButton',
    inject: {
        buttonGroupComponent: { default: null },
    },
    computed: {
        isRouterLink() {
            return Boolean(this.to);
        },
        isLink() {
            return typeof this.$attrs.href !== 'undefined';
        },
        isButton() {
            return this.tagName === 'button';
        },
        tagName() {
            let tag = this.tag || 'button';

            if (this.isRouterLink) {
                tag = 'router-link';
            } else if (this.isLink) {
                tag = 'a';
            }

            return tag;
        },
        isButtonGroupDisabled() {
            return this.buttonGroupComponent?.disabled;
        },
        isBaseButtonDisabled() {
            return Boolean(this.disabled || this.isButtonGroupDisabled);
        },
        buttonClasses() {
            return [
                {
                    disabled: this.isBaseButtonDisabled,
                },
            ];
        },
        attrs() {
            const attrs = { ...this.$attrs };

            if (this.isRouterLink) {
                attrs.to = this.to;
                attrs.tag = this.tag;
            } else if (this.isButton) {
                attrs.type = attrs.type || 'button';
            }

            if (this.isBaseButtonDisabled) {
                if (this.isButton) {
                    attrs.disabled = 'disabled';
                }
            }

            return attrs;
        },
        listeners() {
            return {
                click: this.buttonClickHandler,
                keyup: this.buttonKeyupHandler,
            };
        },
    },
    methods: {
        buttonBlur() {
            this.$refs.button?.blur();
        },
        buttonClickHandler() {
            this.buttonBlur();
        },
        buttonKeyupHandler(e) {
            if (e.key === 'Escape' || e.key === 'Esc') {
                this.buttonBlur();
            }
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Отключает взаимодействие с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет тег HTML для кнопки
         */
        tag: {
            type: String,
            default: 'button',
        },
        /**
         * Роут для `<RouterLink>` для перехода на другую страницу без перезагрузки.
         * Может быть строкой, либо объектом.
         * Имеет более высокий приоритет перед `href`
         */
        to: {
            type: [Object, String],
            default: null,
        },
    },
};
</script>

<style lang="scss">
.sbs-base-button {
    margin: 0;
    padding: 0;
    color: $current-color;
    font-family: inherit;
    text-decoration: none;
    background-color: $transparent;
    border-width: 0;
    outline: none;
    outline-offset: 3px;
    cursor: pointer;

    &.disabled {
        cursor: not-allowed;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &:not(.disabled):active {
        opacity: 0.6;
    }

    &:visited {
        color: $current-color;
    }
}
</style>
